import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

export interface ModuleIntroModalProps {
  isOpen: boolean;
  onClose: () => void;
  moduleType: 'decks' | 'quizzes' | 'matches';
}

interface ModuleContent {
  icon: string;
  title: string;
  description: string;
  features: string[];
  gradient: string;
}

const moduleContents: Record<string, ModuleContent> = {
  decks: {
    icon: '🃏',
    title: 'Bem-vindo aos Flashcards!',
    description: 'Flashcards são coleções organizadas de cartões de estudo que ajudam você a aprender de forma eficiente.',
    features: [
      'Crie flashcards personalizados sobre qualquer assunto',
      'Adicione cartões com perguntas e respostas',
      'Organize seus estudos por temas e tags',
      'Use o sistema de repetição espaçada para memorização eficaz',
      'Compartilhe e descubra flashcards públicos da comunidade'
    ],
    gradient: 'from-blue-500 to-purple-600'
  },
  quizzes: {
    icon: '🎯',
    title: 'Bem-vindo aos Quizzes!',
    description: 'Quizzes são testes interativos que ajudam você a avaliar seu conhecimento de forma divertida.',
    features: [
      'Crie quizzes baseados em seus flashcards',
      'Escolha entre múltipla escolha, verdadeiro/falso e mais',
      'Acompanhe seu desempenho e progresso',
      'Revise respostas incorretas para melhorar',
      'Desafie-se com limites de tempo personalizados'
    ],
    gradient: 'from-green-500 to-teal-600'
  },
  matches: {
    icon: '🎮',
    title: 'Bem-vindo ao Match!',
    description: 'Match é um jogo divertido de memória onde você associa pares de conceitos para aprender.',
    features: [
      'Jogue combinando termos e definições',
      'Teste sua velocidade e memória',
      'Crie jogos baseados em seus flashcards',
      'Acompanhe seu melhor tempo e desempenho',
      'Aprenda de forma lúdica e envolvente'
    ],
    gradient: 'from-orange-500 to-red-600'
  }
};

const ModuleIntroModal: React.FC<ModuleIntroModalProps> = ({ isOpen, onClose, moduleType }) => {
  if (!isOpen) return null;

  const content = moduleContents[moduleType];

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-black bg-opacity-60 backdrop-blur-sm"
          onClick={onClose}
        ></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full border border-gray-200 dark:border-gray-700">
          {/* Header with gradient background */}
          <div className={`bg-gradient-to-r ${content.gradient} px-6 py-8 text-center relative`}>
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-white hover:text-gray-200 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
            
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg text-5xl">
                {content.icon}
              </div>
            </div>
            
            <h3 className="text-3xl font-bold text-white mb-3">
              {content.title}
            </h3>
            
            <p className="text-white text-lg opacity-95 max-w-xl mx-auto">
              {content.description}
            </p>
          </div>

          {/* Content */}
          <div className="px-8 py-6 bg-white dark:bg-gray-800">
            <div className="mb-6">
              <h4 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                O que você pode fazer:
              </h4>
              
              <ul className="space-y-3">
                {content.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <svg
                      className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-0.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="text-gray-700 dark:text-gray-300 text-base">
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* CTA Button */}
            <div className="flex justify-center pt-4">
              <button
                onClick={onClose}
                className={`bg-gradient-to-r ${content.gradient} text-white px-8 py-3 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200`}
              >
                Começar Agora
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModuleIntroModal;
